/** Extracts tilemap creation, layer setup, camera config, building system, and projectile pool from GameScene. */
import Phaser from 'phaser';
import { CAMERA_LERP, CAMERA_ZOOM, CANVAS_SCALE, CURRENT_ZONE, DPR, GAME_WIDTH, MAP_HEIGHT_PX, MAP_WIDTH_PX } from '../utils/Constants';
import { tileToWorld, worldToTile } from '../utils/Math';
import { ConfigLoader } from '../systems/ConfigLoader';
import type { SectorConfig } from '../systems/ConfigLoader';
import { WallPhysicsSystem } from '../systems/WallPhysicsSystem';
import { BuildingSystem } from '../systems/BuildingSystem';
import { ProjectilePool } from '../systems/ProjectilePool';
import { DebugConfig } from '../debug/DebugConfig';
import { DebugLog } from '../debug/DebugLog';
import { SaveSystem } from '../systems/SaveSystem';

// Tile collision indices: empty (0), wall (3), rubble (4-19), perimeter edges (20-31)
const COLLISION_TILE_EMPTY = 0;
const COLLISION_TILE_WALL_START = 3;
const COLLISION_TILE_WALL_END = 31;

// Rubble tile range for LOS transparency
const RUBBLE_TILE_START = 4;
const RUBBLE_TILE_END = 19;

// Tiled GID used as a fallback spawn marker in the tilemap
const SPAWN_MARKER_GID = 848;

/** Result bag returned after bootstrapping the scene. */
export interface BootstrapResult {
  map: Phaser.Tilemaps.Tilemap;
  groundLayer: Phaser.Tilemaps.TilemapLayer;
  wallPhysics: WallPhysicsSystem;
  buildingSystem: BuildingSystem;
  projectilePool: ProjectilePool;
  sectorConfig: SectorConfig | undefined;
  spawnX: number;
  spawnY: number;
}

/** Bootstrap the tilemap, ground layer, camera, buildings, projectile pool, and spawn position. */
export function bootstrapScene(
  scene: Phaser.Scene,
  saveSystem: SaveSystem,
  saveName: string,
  player: null, // player created after bootstrap returns spawnX/spawnY
): Omit<BootstrapResult, 'spawnX' | 'spawnY'> & { spawnX: number; spawnY: number } {
  DebugConfig.load();

  const map = scene.make.tilemap({ key: CURRENT_ZONE });
  const tileset = map.addTilesetImage('earth-tileset', 'earth-tileset');
  if (!tileset) throw new Error('Failed to load tileset');

  const layer = map.createLayer('Ground', tileset, 0, 0);
  if (!layer) throw new Error('Failed to create Ground layer');

  const groundLayer = layer;
  groundLayer.setScale(0.5); // 64px tiles render at 32px world size
  const wallPhysics = new WallPhysicsSystem(groundLayer);

  const mapWidthPx = MAP_WIDTH_PX;
  const mapHeightPx = MAP_HEIGHT_PX;

  // --- Perimeter rubble system ---
  const PERIMETER_KEY = 'rubble-perimeter-a';
  const RUBBLE_ANGLE = 30;
  const PAD = GAME_WIDTH;
  const rubbleBg = scene.add.tileSprite(
    mapWidthPx / 2, mapHeightPx / 2,
    (mapWidthPx + PAD * 2) * 1.5, (mapHeightPx + PAD * 2) * 1.5,
    PERIMETER_KEY
  );
  rubbleBg.setOrigin(0.5, 0.5);
  rubbleBg.setTileScale(0.04);
  rubbleBg.setAngle(RUBBLE_ANGLE);
  rubbleBg.setDepth(-1);

  // Determine spawn position
  const existingSave = saveSystem.loadRun(saveName);
  let spawnX: number;
  let spawnY: number;

  const sectorConfig = ConfigLoader.getSectorConfig(CURRENT_ZONE);
  if (existingSave) {
    spawnX = existingSave.position.x;
    spawnY = existingSave.position.y;

    // Validate saved position is walkable — fall back to hubSpawn if inside a wall
    if (!wallPhysics.isPositionWalkable(spawnX, spawnY)) {
      if (sectorConfig) {
        spawnX = sectorConfig.hubSpawn.x;
        spawnY = sectorConfig.hubSpawn.y;
      } else {
        const spawnTile = findSpawnTile(map, groundLayer);
        spawnX = spawnTile ? tileToWorld(spawnTile.x) : mapWidthPx / 2;
        spawnY = spawnTile ? tileToWorld(spawnTile.y) : mapHeightPx / 2;
      }
    }
  } else if (sectorConfig) {
    spawnX = sectorConfig.hubSpawn.x;
    spawnY = sectorConfig.hubSpawn.y;
  } else {
    const spawnTile = findSpawnTile(map, groundLayer);
    spawnX = spawnTile ? tileToWorld(spawnTile.x) : mapWidthPx / 2;
    spawnY = spawnTile ? tileToWorld(spawnTile.y) : mapHeightPx / 2;
  }

  // Building system
  const buildingSystem = new BuildingSystem(scene, groundLayer);

  // Projectile pool
  const projectilePool = new ProjectilePool(scene, groundLayer);

  return { map, groundLayer, wallPhysics, buildingSystem, projectilePool, sectorConfig, spawnX, spawnY };
}

/** Configure camera: smooth follow with tilemap pixel-snapping. */
export function setupCamera(
  scene: Phaser.Scene,
  groundLayer: Phaser.Tilemaps.TilemapLayer,
  playerSprite: Phaser.GameObjects.GameObject,
): void {
  const PAD = GAME_WIDTH;
  const cam = scene.cameras.main;
  cam.setZoom(DPR * CANVAS_SCALE * CAMERA_ZOOM);
  cam.startFollow(playerSprite, true, CAMERA_LERP, CAMERA_LERP);
  cam.setBounds(-PAD, -PAD, MAP_WIDTH_PX + PAD * 2, MAP_HEIGHT_PX + PAD * 2);
}

/** Wire collision tiles and mark rubble as LOS-transparent. */
export function setupCollisionTiles(
  scene: Phaser.Scene,
  groundLayer: Phaser.Tilemaps.TilemapLayer,
  playerSprite: Phaser.GameObjects.GameObject,
): void {
  const collisionTiles = [COLLISION_TILE_EMPTY, ...Array.from({ length: COLLISION_TILE_WALL_END - COLLISION_TILE_WALL_START + 1 }, (_, i) => i + COLLISION_TILE_WALL_START)];
  groundLayer.setCollision(collisionTiles);
  scene.physics.add.collider(playerSprite, groundLayer);

  // Debug: log tile coords on click when physics logging is enabled
  scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
    if (!DebugConfig.flags.physicsLogging) return;
    const wx = pointer.worldX;
    const wy = pointer.worldY;
    const tc = worldToTile(wx);
    const tr = worldToTile(wy);
    const tile = groundLayer.getTileAt(tc, tr);
    console.log(`Tile (${tc}, ${tr}) = ${tile ? tile.index : 'null'}  world (${Math.round(wx)}, ${Math.round(wy)})`);
  });

  // Mark rubble tiles as LOS-transparent
  groundLayer.forEachTile((tile) => {
    if (tile.index >= RUBBLE_TILE_START && tile.index <= RUBBLE_TILE_END) {
      tile.properties = { losTransparent: true };
    }
  });
}

/** Find the fallback spawn marker tile (SPAWN_MARKER_GID) in the tilemap. */
export function findSpawnTile(
  map: Phaser.Tilemaps.Tilemap,
  groundLayer: Phaser.Tilemaps.TilemapLayer,
): Phaser.Tilemaps.Tile | null {
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const tile = groundLayer.getTileAt(x, y);
      if (tile && tile.index === SPAWN_MARKER_GID) {
        return tile;
      }
    }
  }
  return null;
}
